import SortChangeEvent from '@adobe-fonts/site/events/SortChangeEvent.js';
import { EVENT_NAME as EditLibraryNameEventName } from '@adobe-fonts/site/events/EditLibraryNameEvent.js';
import { EVENT_NAME as LibraryFollowEventName } from '@adobe-fonts/site/events/LibraryFollowEvent.js';
import { EVENT_NAME as USER_PREFS_EVENT } from '@adobe-fonts/site/events/UserPrefsEvent.js';

import NotificationEvent from '@adobe-fonts/site/events/NotificationEvent.js';
import RemoveLibraryFontEvent, { RemoveLibraryFontEventType } from '@adobe-fonts/site/events/RemoveLibraryFontEvent.js';

import { registerEventHandlers as registerFontSizeHandlers } from './mixins/settings-bar/FontSizeChange.js';
import { registerEventHandlers as registerSampleTextHandlers } from './mixins/settings-bar/SampleTextChange.js';
import { registerEventHandlers as registerSaveToLibraryHandlers } from './mixins/SaveToLibrary.js';
import { registerEventHandlers as registerVariationCardAddToWebProjects } from './mixins/variation-card/VariationCardAddToWebProject.js';
import { registerEventHandlers as registerShareableLibraryHandlers } from './mixins/ShareableLibrary.js';
import { registerEventHandlers as registerVariationCardCheckboxHandlers } from './mixins/variation-card/VariationCardCheckbox.js';
import { registerEventHandlers as registerVariationCardViewHandlers } from './mixins/variation-card/VariationCardView.js';
import { registerEventHandlers as registerVariationCardFavoriteHandlers } from './mixins/variation-card/VariationCardFavorite.js';
import { registerEventHandlers as registerVariationCardSyncHandlers, EVENT_NAME as VARIATION_CARD_SYNC_EVENT } from './mixins/variation-card/VariationCardSync.js';
import { registerEventHandlers as registerViewOptionHandlers } from './mixins/settings-bar/ViewOptionChange.js';
import { registerEventHandlers as registerLibrariesToolbarCloseHandlers } from './mixins/LibrariesToolbarClose.js';
import { registerEventHandlers as registerLibrariesToolbarSyncHandlers } from './mixins/LibrariesToolbarSync.js';
import { registerEventHandlers as registerLibrariesToolbarFavoriteHandlers } from './mixins/LibrariesToolbarFavorite.js';
import { registerEventHandlers as registerDeleteLibraryHandlers } from './mixins/DeleteLibrary.js';
import { registerEventHandlers as registerUserPrefsHandlers } from './mixins/UserPrefs.js';

import { getService as service } from '../util/angular_helpers.js';
import { registerEventHandlers as registerCoachmarkHandlers } from '../util/coachmark_helpers.js';
import { addPageAction, getNewRelicName, handlePageClick } from '../util/new_relic.js';
import PostLoginActionProcessor from '../util/post_login_actions/post_login_action_processor.js';
import { checkBookmarks } from '../util/libraries.js';
import { getData } from '../util/script_data.js';

const abortController = new AbortController();
const page = 'library-detail-page';

class LibraryShowPage extends HTMLElement {
  constructor() {
    super();
    this.checkedVariationsCount = 0;
    this.sortOrder = '-created_date';
    this.currentPage = 1;
    this.allVariationsLoaded = false;
    this.libraryId = window.location.pathname.split('/').pop();
    this.libraryAnalyticsAttributes = {
      libraryId: this.libraryId,
      isLibraryPublic: this.isLibraryPublic
    };
    this._setFontCount(this._initialFontCount);
    this._toggleShowMoreButton();
  }

  connectedCallback() {
    registerCoachmarkHandlers(this);
    registerFontSizeHandlers(this);
    registerSampleTextHandlers(this);
    registerSaveToLibraryHandlers(this);
    registerVariationCardAddToWebProjects(this);
    registerVariationCardCheckboxHandlers(this);
    registerVariationCardSyncHandlers(this);
    registerVariationCardViewHandlers(this);
    registerVariationCardFavoriteHandlers(this);
    registerViewOptionHandlers(this);
    registerLibrariesToolbarCloseHandlers(this);
    registerLibrariesToolbarSyncHandlers(this);
    registerLibrariesToolbarFavoriteHandlers(this);
    registerDeleteLibraryHandlers(this);
    registerUserPrefsHandlers(this);
    registerShareableLibraryHandlers(this);

    window.addEventListener('load', this.handlePageLoad);
    window.addEventListener('DOMContentLoaded', () => {
      if (this.isLibraryPublic === 'true') {
        this.libraryAnalyticsAttributes = {
          ...this.libraryAnalyticsAttributes,
          copyEnabled: this._visitorCopyButton !== null || this._copyLibraryButton !== null,
          followEnabled: this._visitorfollowLibraryButton !== null || this._followLibraryButton !== null,
          isFollowing: !!this._followLibraryButton?.followed
        };
      }
      addPageAction(`typekit.${page}.page_load`, this.libraryAnalyticsAttributes);
    }, { once: true });

    this.addEventListener(RemoveLibraryFontEvent.EVENT_NAME, this.handleRemoveLibraryFontEvent);
    this.addEventListener(SortChangeEvent.EVENT_NAME, this.handleSortVariations);
    this.addEventListener(EditLibraryNameEventName, this.handleEditLibraryName);
    this.addEventListener(VARIATION_CARD_SYNC_EVENT, this.handleSyncStateChangeEvent);
    if (this._showMoreButton) {
      this._showMoreButton.addEventListener('click', () => this.handleShowMoreVariations());
    }

    if (this._visitorfollowLibraryButton) {
      this._visitorfollowLibraryButton.addEventListener('click', () => this.handleAuthenticationAction('follow_library'), { once: true });
    }

    if (this._visitorCopyButton) {
      this._visitorCopyButton.addEventListener('click', () => this.handleAuthenticationAction('copy_library'), { once: true });
    }

    if (this._followLibraryButton) {
      this.handleFollowButtonState();
    }

    this.initAnalytics();
    window.addEventListener('beforeunload', () => {
      addPageAction(`typekit.${page}.page_exit`, { isLibraryPublic: this.isLibraryPublic });

      // remove event listeners that could happen more than once
      // (i.e. listeners that do not navigate users away from the current page)
      abortController.abort();
    });

    if (this._addAllFontsButtons.length) {
      this._addAllFontsButtons.forEach(button => {
        if (button.getAttribute('?synced') === 'true') {
          button.toggleAttribute('disabled', true);
        }
        button.addEventListener('click', this.handleAddAllFonts);
      });
    }

    window.addEventListener('library-bookmarked', this.handlePublicLibraryBreadcrumb);
    window.addEventListener(LibraryFollowEventName, this.handlePublicLibraryBreadcrumb);
    window.addEventListener(USER_PREFS_EVENT, this.handleUserPrefsChange);
  }

  disconnectedCallback() {
    window.removeEventListener('load', this.handlePageLoad);
    window.removeEventListener(LibraryFollowEventName, this.handlePublicLibraryBreadcrumb);
    window.removeEventListener('library-bookmarked', this.handlePublicLibraryBreadcrumb);
    this.removeEventListener(SortChangeEvent.EVENT_NAME, this.handleSortVariations);
    this.removeEventListener(EditLibraryNameEventName, this.handleEditLibraryName);
    window.removeEventListener(USER_PREFS_EVENT, this.handleUserPrefsChange);
    if (this._showMoreButton) {
      this._showMoreButton.removeEventListener('click', () => this.handleShowMoreVariations());
    }
  }

  get isLibraryPublic() {
    return this.getAttribute('is-library-public');
  }

  get _initialFontCount() {
    return this.getAttribute('initial-font-count') || 0;
  }

  get _variationCards() {
    return this.querySelectorAll('af-variation-card');
  }

  get _variationsListContainer() {
    return this.querySelector('#library__font-variations');
  }

  get _librariesToolbar() {
    return this.querySelector('af-libraries-toolbar');
  }

  get _settingsBar() {
    return this.querySelector('.settings-bar');
  }

  get _shareLibraryButtons() {
    return this.querySelectorAll('.share-library-btn');
  }

  get _showMoreButton() {
    return this.querySelector('#show-more-variations');
  }

  get _libraryTitles() {
    return this.querySelectorAll('[data-library-name]');
  }

  get _isListView() {
    return !!this._variationsListContainer.hasAttribute('listview');
  }

  get _addAllFontsButtons() {
    return this.querySelectorAll('.add-all-button');
  }

  /**
   * Visitors (not logged in users) will see these buttons to copy and follow the library.
   */
  get _visitorCopyButton() {
    return this.querySelector('#copy-library');
  }

  get _visitorfollowLibraryButton() {
    return this.querySelector('#follow-library');
  }

  /**
   * This is the actual copy and follow library button for logged in users.
   */
  get _copyLibraryButton() {
    return this.querySelector('#copy-library-trigger');
  }

  get _followLibraryButton() {
    return this.querySelector('[data-id="follow-library-button"]');
  }

  get _sharedLibraryBreadcrumb() {
    return this.querySelector('.shared-library-breadcrumb');
  }

  get _followingLibraryBreadcrumb() {
    return this.querySelector('.following-library-breadcrumb');
  }

  handleFollowButtonState = () => {
    checkBookmarks(this._followLibraryButton);
  };

  handlePublicLibraryBreadcrumb = () => {
    const following = this._followLibraryButton.followed;
    this._sharedLibraryBreadcrumb.style.display = following ? 'none' : 'flex';
    this._followingLibraryBreadcrumb.style.display = following ? 'flex' : 'none';
  };

  handleAuthenticationAction = async (actionName, attributes = {}) => {
    const AuthenticationService = service('AuthenticationService');
    await AuthenticationService.handleAuthenticationWithPostLoginAction(actionName, { ...attributes, publicLibraryId: this.libraryId });
  };

  handleAddAllFonts = async () => {
    if (this._addAllFontsButtons?.[0]?.hasAttribute('disabled')) {
      return;
    }
    this._addAllFontsButtons.forEach(button => button.toggleAttribute('disabled', true));

    try {
      const fontIdsByBatch = await this.getAllVariationIdsByBatch();
      await this.handleAuthenticationAction('activate_fonts', { font_ids: fontIdsByBatch.flat() });
      const { addedFontIds, message } = await this.addAllFonts(fontIdsByBatch);

      if (addedFontIds.length) {
        addedFontIds.forEach((fontId) => {
          const variationSyncButton = this.querySelector(`af-variation-card[data-id="${fontId}"] af-sync-button`);
          if (variationSyncButton) {
            variationSyncButton.toggleAttribute('synced', true);
          }
        });
        this.showSyncSuccessMessage();
        addPageAction(`typekit.${page}.add-all-fonts.click`, { ...this.libraryAnalyticsAttributes, fontCount: addedFontIds.length });
      } else {
        if (message) {
          this.dispatchEvent(new NotificationEvent({ type: 'error', message }));
        } else {
          this.showSyncErrorMessage();
        }
        this._addAllFontsButtons.forEach(button => button.toggleAttribute('disabled', false));
      }
    } catch (e) {
      this.showSyncErrorMessage();
      this._addAllFontsButtons.forEach(button => button.toggleAttribute('disabled', false));
    }
  };

  handleEditLibraryName = async (event) => {
    if (!event.detail.name) {
      return;
    }

    const newName = event.detail.name;

    this._libraryTitles.forEach((title) => {
      title.textContent = newName;
    });

    document.title = newName;
  };

  handleUserPrefsChange = async (event) => {
    const removeActionButtons = this.querySelectorAll('af-variation-card-actions-menu');
    removeActionButtons.forEach((element) => {
      element.hideRemoveLibraryFontDialog = event.detail.hideRemoveFromLibraryDialog;
    });
  };

  handleRemoveLibraryFontEvent = (event) => {
    if (event.detail.type === RemoveLibraryFontEventType.Restore) {
      this._setFontCount(this.fontCount + 1);
    } else {
      this._setFontCount(this.fontCount - 1);
    }
  };

  handleSortVariations = async (event) => {
    if (!(event instanceof SortChangeEvent)) {
      return;
    }

    this.sortOrder = event.detail.sortOrder;
    this.currentPage = 1;
    const variationCards = await this.fetchData();

    this._variationsListContainer.replaceChildren(...variationCards.body.children);

    this._toggleShowMoreButton();
  };

  handleShowMoreVariations = async (throwError) => {
    this.currentPage++;
    if (this._showMoreButton) {
      this._showMoreButton.toggleAttribute('disabled', true);
    }

    try {
      const variationCards = await this.fetchData();
      this._variationsListContainer.append(...variationCards.body.children);
      this._toggleShowMoreButton();
    } catch (e) {
      console.error('Error fetching more variations', e);
      if (throwError) {
        throw e;
      }
    } finally {
      if (this._showMoreButton) {
        this._showMoreButton.toggleAttribute('disabled', false);
      }
    }
  };

  handleSyncStateChangeEvent = () => {
    this.updateAddAllFontButtonState();
  };

  updateAddAllFontButtonState = () => {
    if (!this._addAllFontsButtons.length) {
      return;
    }

    const unsynced = this.querySelectorAll('af-variation-card af-sync-button:not([synced])').length > 0;
    this._addAllFontsButtons.forEach(button => button.toggleAttribute('disabled', !unsynced));
  };

  fetchData = async () => {
    const libraryId = window.location.pathname.split('/').pop();
    const response = await fetch(`/libraries/${libraryId}.json?current_page=${this.currentPage}&sort_order=${this.sortOrder}`);
    const data = await response.json();

    const variationCards = new DOMParser().parseFromString(data.content.variationCards, 'text/html');

    let sampleText = document.querySelector('af-sample-text-menu')?.shadowRoot?.querySelector('sp-combobox')?.value;
    let sampleTextSize = document.querySelector('af-font-size-slider')?.shadowRoot?.querySelector('sp-slider')?.value;

    const displayFonts = variationCards.querySelectorAll('af-display-in-font');
    displayFonts.forEach(function(sample) {
      if (sampleTextSize) {
        sample.style.setProperty('font-size', `${sampleTextSize}px`);
      }
      if (sampleText) {
        sample.setAttribute('previewstring', sampleText);
        sample.innerText = sampleText;
      }
    });

    if (this._isListView) {
      Array.from(variationCards.body.children).forEach(variationCard => {
        variationCard.setAttribute('listview', this._isListView);
      });
    }

    return variationCards;
  };

  getAllVariationIdsByBatch = async () => {
    const libraryId = window.location.pathname.split('/').pop();
    const fontIdsByBatch = [];
    let shouldShowMore = true;
    let currentPage = 1;
    while (shouldShowMore) {
      const response = await fetch(`/libraries/${libraryId}.json?current_page=${currentPage}`);
      const data = await response.json();
      shouldShowMore = data.content.hasMoreVariations;
      if (!data.variations.length) {
        break;
      }
      const fontIds = data.variations.filter(variation => !variation.is_synced).map(variation => variation.id);
      if (fontIds.length) {
        fontIdsByBatch.push(fontIds);
      }
      currentPage++;
    }

    return fontIdsByBatch;
  };

  addAllFonts = async (fontIdsByBatch) => {
    const addedFontIds = [];
    let message;
    for (const fontIds of fontIdsByBatch) {
      if (!fontIds.length) {
        return;
      }
      const response = await this.addFonts(fontIds);
      addedFontIds.push(...response.data?.added_font_ids || []);
      message = response.data?.message;
    }
    return { addedFontIds, message };
  };

  addFonts = async (fontIds) => {
    const syncService = service('SyncService');

    return syncService.addSyncedVariations(fontIds);
  };

  showSyncSuccessMessage() {
    const i18n = getData('/neue/i18n');
    const firstMileService = service('FirstMileService');
    firstMileService.onUseModelFontActivation(i18n);
  }

  showSyncErrorMessage() {
    const i18n = (getData('/neue/i18n') || {})['neue.browse.family_card'] || {};
    this.dispatchEvent(new NotificationEvent({
      type: 'error',
      message: i18n['error_occurred_adding_fonts'],
      options: {
        actionLabel: i18n['try_again'],
        callbackAction: () => this.handleAddAllFonts()
      }
    }));
  }

  async handlePageLoad() {
    PostLoginActionProcessor.handlePageLoad();
  }

  /**
   * Initialize analytics for the library show page (or library detail page).
   * This method sets up listeners for elements that are unique to this page.
   * For elements that are shared across multiple pages, the analytics are fired from either within
   * the mixin / component (AFS) itself or in a dedicated controller.
   */
  initAnalytics = () => {
    // Events that could occur in both private and public libraries
    this.addEventListener('click', (event) => {
      handlePageClick(event, page);
    }, { signal: abortController.signal });

    // private library events
    if (this._shareLibraryButtons.length > 0) {
      this._shareLibraryButtons.forEach((button) => {
        this.addClickAnalyticsEventListener(button, 'share-library-btn');
      });
    }

    // public library events
    // visitor and logged in user clicks send the same action name for copy & follow library
    if (this._copyLibraryButton) {
      this.addClickAnalyticsEventListener(this._copyLibraryButton, 'copy-library');
    }

    if (this._followLibraryButton) {
      this._followLibraryButton.addEventListener('click', (event) => {
        const isFollowing = this._followLibraryButton.followed;
        const actionName = isFollowing ? 'unfollow-library' : 'follow-library';
        addPageAction(getNewRelicName(page, event, actionName), this.libraryAnalyticsAttributes);
      }, { signal: abortController.signal });
    }

    if (this._visitorCopyButton) {
      this.addClickAnalyticsEventListener(this._visitorCopyButton, 'copy-library');
    }

    if (this._visitorfollowLibraryButton) {
      this.addClickAnalyticsEventListener(this._visitorfollowLibraryButton, 'follow-library');
    }
  };

  addClickAnalyticsEventListener = (element, actionName) => {
    if (element) {
      element.addEventListener('click', (event) => {
        addPageAction(getNewRelicName(page, event, actionName), this.libraryAnalyticsAttributes);
      }, { signal: abortController.signal });
    }
  };

  _setFontCount(count) {
    this.fontCount = count;
    const i18n = getData('/neue/i18n');
    if (!i18n) {
      return;
    }
    const translationString = this.fontCount === 1 ? i18n['neue.shared'].font : i18n['neue.shared'].fonts;
    this.querySelector('#font_count').textContent = translationString.replace('%{number}', this.fontCount);
  }

  _toggleShowMoreButton = () => {
    this.allVariationsLoaded = parseInt(this.fontCount) === this._variationCards.length;

    if (this._showMoreButton) {
      this._showMoreButton.style.display = this.allVariationsLoaded ? 'none' : 'inline-flex';
    }
  };
}

customElements.define('af-library-show-page', LibraryShowPage);

export { LibraryShowPage };
